"use client";

import { CinematicModal } from "@/components/ui/CinematicModal";
import type { Flashcard } from "@/hooks/useFlashcards";
import {
  getFlashcardLocale,
  speakFlashcardText,
  stopFlashcardPronunciation,
} from "@/lib/flashcards/pronunciation";
import { getSavedFlashcardBack, getSavedFlashcardFront } from "@/lib/sessionVocabulary";
import { ChevronLeft, ChevronRight, Heart, RotateCcw, Trash2, Volume2, X } from "lucide-react";
import * as React from "react";

interface FlashcardReviewCardProps {
  flashcard: Flashcard;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onToggleFavorite: (id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  currentIndex: number;
  totalCount: number;
  fallbackFocusRef?: React.RefObject<HTMLElement | null>;
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-white/10 py-4 first:border-t-0 first:pt-0">
      <h3 className="mb-2 text-xs font-bold text-orange-300">{title}</h3>
      {children}
    </section>
  );
}

const actionClass =
  "flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70 disabled:cursor-not-allowed disabled:text-white/25";

export function FlashcardReviewCard({
  flashcard,
  onClose,
  onNext,
  onPrevious,
  onToggleFavorite,
  onDelete,
  currentIndex,
  totalCount,
  fallbackFocusRef,
}: FlashcardReviewCardProps) {
  const [face, setFace] = React.useState({ id: flashcard.id, flipped: false });
  const isFlipped = face.id === flashcard.id && face.flipped;
  const [deleting, setDeleting] = React.useState(false);
  const [favoriting, setFavoriting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const frontText = getSavedFlashcardFront(flashcard);
  const backText = getSavedFlashcardBack(flashcard);
  const isImported = flashcard.card_type === "imported";
  const targetLocale = getFlashcardLocale(flashcard.target_language);
  const instructionLocale = getFlashcardLocale(flashcard.instruction_language);
  const titleId = React.useId();

  React.useEffect(() => {
    contentRef.current?.focus();
  }, [flashcard.id, isFlipped]);

  React.useEffect(() => {
    setError(null);
    return stopFlashcardPronunciation;
  }, [flashcard.id]);

  const toggleFace = () => setFace({ id: flashcard.id, flipped: !isFlipped });

  const handleToggleFavorite = async () => {
    if (favoriting) return;
    setFavoriting(true);
    try {
      await onToggleFavorite(flashcard.id);
    } catch {
      setError("We couldn't update your favorite. Please try again.");
    } finally {
      setFavoriting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting || !confirm("Delete this flashcard?")) return;
    setDeleting(true);
    try {
      await onDelete(flashcard.id);
      onClose();
    } catch {
      setError("We couldn't delete this card. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // VoiceOver and other assistive-technology modifier commands keep ownership.
    if (
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      (event.target as HTMLElement).closest("input, select, textarea, [contenteditable=true]")
    )
      return;
    if (event.key === "ArrowRight" && onNext) {
      event.preventDefault();
      onNext();
    }
    if (event.key === "ArrowLeft" && onPrevious) {
      event.preventDefault();
      onPrevious();
    }
  };

  return (
    <CinematicModal
      isOpen
      onClose={onClose}
      maxWidth="2xl"
      titleId={titleId}
      showCloseButton={false}
      initialFocusRef={contentRef}
      fallbackFocusRef={fallbackFocusRef}
      contentClassName="flex min-h-0 flex-col p-0 sm:p-0"
    >
      <div onKeyDown={handleKeyDown} className="flex min-h-0 flex-col text-white">
        <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-3 py-3 sm:px-5">
          <div className="min-w-0">
            <h2 id={titleId} className="text-sm font-bold">
              {flashcard.target_language} flashcard review
            </h2>
            <p role="status" className="mt-1 text-xs text-white/55">
              Card {currentIndex + 1} of {totalCount}
            </p>
          </div>
          <div className="flex shrink-0 items-center">
            <button
              type="button"
              className={actionClass}
              aria-label="Hear pronunciation"
              title="Hear pronunciation"
              onClick={() => {
                setError(null);
                speakFlashcardText(frontText, flashcard.target_language, setError);
              }}
            >
              <Volume2 size={18} />
            </button>
            <button
              type="button"
              className={actionClass}
              onClick={handleToggleFavorite}
              disabled={favoriting}
              aria-pressed={flashcard.is_favorite}
              aria-label={flashcard.is_favorite ? "Remove from favorites" : "Add to favorites"}
            >
              <Heart
                size={18}
                className={flashcard.is_favorite ? "fill-orange-400 text-orange-400" : ""}
              />
            </button>
            <button
              type="button"
              className={actionClass}
              onClick={handleDelete}
              disabled={deleting}
              aria-label="Delete flashcard"
            >
              <Trash2 size={17} />
            </button>
            <button
              type="button"
              className={actionClass}
              onClick={onClose}
              aria-label="Close review"
            >
              <X size={19} />
            </button>
          </div>
        </header>
        {error && (
          <p role="alert" className="border-b border-white/10 px-5 py-3 text-sm text-orange-200">
            {error}
          </p>
        )}
        <div className="min-h-0 overflow-y-auto overscroll-contain px-5 py-6 sm:px-7">
          <div
            ref={contentRef}
            role="region"
            aria-label={isFlipped ? "Answer" : "Word or phrase"}
            tabIndex={-1}
            className="rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
          >
            {!isFlipped ? (
              <>
                <h3 className="text-xs font-bold text-orange-300">Word or phrase</h3>
                <p
                  lang={targetLocale}
                  className="my-6 break-words text-2xl font-semibold leading-relaxed"
                >
                  {frontText}
                </p>
              </>
            ) : (
              <>
                <h3 className="mb-4 text-base font-bold">Answer</h3>
                {backText && (
                  <DetailSection title={isImported ? "Back" : "Translation"}>
                    <p lang={instructionLocale} className="text-lg leading-relaxed text-white/85">
                      {backText}
                    </p>
                  </DetailSection>
                )}
                {isImported && flashcard.source_deck_name && (
                  <DetailSection title="Deck">
                    <p>{flashcard.source_deck_name}</p>
                  </DetailSection>
                )}
                {!isImported && Object.keys(flashcard.ipa_spelling || {}).length > 0 && (
                  <DetailSection title="Pronunciation">
                    {Object.entries(flashcard.ipa_spelling).map(([word, ipa]) => (
                      <p key={word} className="py-1">
                        <span lang={targetLocale}>{word}</span>{" "}
                        <span className="font-mono text-orange-200">[{ipa}]</span>
                      </p>
                    ))}
                  </DetailSection>
                )}
                {!isImported && !!flashcard.vocabulary?.length && (
                  <DetailSection title="Vocabulary">
                    {flashcard.vocabulary.map((item, index) => (
                      <div key={index} className="py-2">
                        <p>
                          <span lang={targetLocale} className="font-bold text-orange-200">
                            {item.word}
                          </span>{" "}
                          <span lang={instructionLocale}>{item.partOfSpeech}</span>
                        </p>
                        <p lang={instructionLocale} className="mt-1 leading-relaxed text-white/75">
                          {item.definition}
                        </p>
                        {item.usage && (
                          <p lang={instructionLocale} className="mt-1 text-sm text-white/65">
                            {item.usage}
                          </p>
                        )}
                      </div>
                    ))}
                  </DetailSection>
                )}
                {!isImported && !!flashcard.grammar_points?.length && (
                  <DetailSection title="Grammar">
                    {flashcard.grammar_points.map((point, index) => (
                      <div key={index} className="py-2">
                        <p lang={instructionLocale} className="font-semibold">
                          {point.title}
                        </p>
                        <p lang={instructionLocale} className="mt-1 leading-relaxed text-white/75">
                          {point.explanation}
                        </p>
                      </div>
                    ))}
                  </DetailSection>
                )}
                {!isImported && !!flashcard.example_sentences?.length && (
                  <DetailSection title="Examples">
                    {flashcard.example_sentences.map((example, index) => (
                      <div key={index} className="py-2">
                        <p lang={targetLocale} className="leading-relaxed">
                          {example.sentence}
                        </p>
                        <p lang={instructionLocale} className="mt-1 text-sm text-white/65">
                          {example.translation}
                        </p>
                      </div>
                    ))}
                  </DetailSection>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            onClick={toggleFace}
            className="mt-5 flex min-h-11 w-full items-center justify-center gap-2 rounded-lg border border-white/15 px-4 py-3 font-semibold text-orange-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-300/70"
          >
            <RotateCcw size={16} />
            {isFlipped ? "Show word or phrase" : "Reveal answer"}
          </button>
        </div>
        <footer className="grid shrink-0 grid-cols-[3rem_1fr_3rem] items-center border-t border-white/10 px-3 py-2">
          <button
            type="button"
            className={actionClass}
            onClick={onPrevious}
            disabled={!onPrevious}
            aria-label="Previous flashcard"
          >
            <ChevronLeft size={22} />
          </button>
          <span className="text-center text-xs text-white/55">
            {currentIndex + 1} / {totalCount}
          </span>
          <button
            type="button"
            className={actionClass}
            onClick={onNext}
            disabled={!onNext}
            aria-label="Next flashcard"
          >
            <ChevronRight size={22} />
          </button>
        </footer>
      </div>
    </CinematicModal>
  );
}
